import {
    ChangeEvent,
    ClipboardEvent,
    DragEvent,
    KeyboardEvent as ReactKeyboardEvent,
    MouseEvent as ReactMouseEvent,
    useCallback,
    useEffect,
    useRef,
    useState
} from 'react';
import { useTranslation } from 'react-i18next';
import styles from './WidgetStyles.module.css';
import { AttachmentRemoveIcon, DebugJsonCopyIcon, ImageIcon, PlusIcon, ResetIcon, SendIcon, StopIcon } from './WidgetIcons';
import type { TaskPlanState, UserImageUpload } from '../types/chat';
import { TaskPlanPanel } from './TaskPlanPanel';

export type InputAreaProps = {
    textareaId: string;
    value: string;
    onChange: (value: string) => void;
    onSend: () => void;
    onCancel?: () => void;
    onReset?: () => void;
    isStreaming?: boolean;
    disabled?: boolean;
    images?: UserImageUpload[];
    onAttachImages?: (files: File[]) => void;
    onRemoveImage?: (imageId: string) => void;
    imageError?: string | null;
    showMediaControls?: boolean;
    taskPlan?: TaskPlanState | null;
    showDebugCopyButton?: boolean;
    onCopyConversationResponses?: () => void | Promise<void>;
    hasConversationResponses?: boolean;
};

const MIN_VISIBLE_LINES = 1;
const MAX_VISIBLE_LINES = 11;

export function InputArea({
    textareaId,
    value,
    onChange,
    onSend,
    onCancel,
    onReset,
    isStreaming = false,
    disabled = false,
    images = [],
    onAttachImages,
    onRemoveImage,
    imageError,
    showMediaControls = true,
    taskPlan = null,
    showDebugCopyButton = false,
    onCopyConversationResponses,
    hasConversationResponses = false
}: InputAreaProps) {
    const { t } = useTranslation();
    const shouldShowTaskPlan = taskPlan?.status === 'active' || taskPlan?.status === 'waiting_for_user';
    const textareaRef = useRef<HTMLTextAreaElement>(null);
    const wasDisabledRef = useRef(disabled);
    const fileInputRef = useRef<HTMLInputElement>(null);
    const imagePreviewCacheRef = useRef(new Map<string, CachedImagePreview>());
    const [isDragging, setIsDragging] = useState(false);
    const [isActionMenuOpen, setIsActionMenuOpen] = useState(false);
    const actionMenuRef = useRef<HTMLDivElement>(null);
    const actionMenuButtonRef = useRef<HTMLButtonElement>(null);

    const syncTextareaSize = useCallback(() => {
        const textarea = textareaRef.current;

        if (!textarea || typeof window === 'undefined') {
            return;
        }

        textarea.style.height = 'auto';

        const computedStyles = window.getComputedStyle(textarea);
        const lineHeight = parseFloat(computedStyles.lineHeight) || 20;
        const paddingTop = parseFloat(computedStyles.paddingTop) || 0;
        const paddingBottom = parseFloat(computedStyles.paddingBottom) || 0;
        const borderTop = parseFloat(computedStyles.borderTopWidth) || 0;
        const borderBottom = parseFloat(computedStyles.borderBottomWidth) || 0;
        const minHeight = lineHeight * MIN_VISIBLE_LINES + paddingTop + paddingBottom + borderTop + borderBottom;
        const maxHeight = lineHeight * MAX_VISIBLE_LINES + paddingTop + paddingBottom + borderTop + borderBottom;
        const scrollHeight = textarea.scrollHeight;
        const nextHeight = Math.min(Math.max(scrollHeight, minHeight), maxHeight);

        textarea.style.height = `${nextHeight}px`;
        textarea.style.overflowY = scrollHeight > maxHeight ? 'auto' : 'hidden';
    }, []);

    useEffect(() => {
        syncTextareaSize();
    }, [value, disabled, syncTextareaSize]);

    useEffect(() => {
        const wasDisabled = wasDisabledRef.current;
        wasDisabledRef.current = disabled;

        if (wasDisabled && !disabled) {
            textareaRef.current?.focus();
        }
    }, [disabled]);

    useEffect(() => {
        const activeImageIds = new Set(images.map((image) => image.id));
        for (const cachedImageId of imagePreviewCacheRef.current.keys()) {
            if (!activeImageIds.has(cachedImageId)) {
                imagePreviewCacheRef.current.delete(cachedImageId);
            }
        }
    }, [images]);

    const handleChange = (event: ChangeEvent<HTMLTextAreaElement>) => {
        onChange(event.target.value);
    };

    const handleKeyDown = (event: ReactKeyboardEvent<HTMLTextAreaElement>) => {
        if (event.key === 'Enter' && !event.shiftKey) {
            event.preventDefault();

            if (!disabled && (value.trim().length > 0 || images.length > 0)) {
                onSend();
            }
        }
    };

    const handlePaste = (event: ClipboardEvent<HTMLTextAreaElement>) => {
        if (!onAttachImages || disabled) {
            return;
        }

        const files: File[] = [];
        for (const item of Array.from(event.clipboardData?.items ?? [])) {
            if (item.kind === 'file') {
                const file = item.getAsFile();
                if (file && file.type.startsWith('image/')) {
                    files.push(file);
                }
            }
        }

        if (files.length) {
            onAttachImages(files);
        }
    };

    const handleSend = () => {
        if (disabled || (!value.trim() && images.length === 0)) {
            return;
        }

        onSend();
    };

    const handleCancel = () => {
        onCancel?.();
    };

    const handleFilesSelected = (event: ChangeEvent<HTMLInputElement>) => {
        if (!event.target.files?.length) {
            return;
        }

        onAttachImages?.(Array.from(event.target.files));
        // reset input value so same file can be picked twice
        event.target.value = '';
    };

    const handleAttachButtonClick = () => {
        if (disabled) {
            return;
        }

        fileInputRef.current?.click();
    };

    const closeActionMenu = useCallback(() => {
        setIsActionMenuOpen(false);
    }, []);

    const handleActionMenuToggle = (event: ReactMouseEvent<HTMLButtonElement>) => {
        event.preventDefault();

        if (disabled || isStreaming) {
            return;
        }

        setIsActionMenuOpen((previous) => !previous);
    };

    useEffect(() => {
        if (!isActionMenuOpen) {
            return;
        }

        const handlePointerDown = (event: MouseEvent) => {
            if (!isMenuPointerEvent(event, actionMenuRef.current, actionMenuButtonRef.current)) {
                closeActionMenu();
            }
        };

        const handleKeyDown = (event: globalThis.KeyboardEvent) => {
            if (event.key === 'Escape') {
                event.stopPropagation();
                closeActionMenu();
            }
        };

        document.addEventListener('mousedown', handlePointerDown);
        document.addEventListener('keydown', handleKeyDown);

        return () => {
            document.removeEventListener('mousedown', handlePointerDown);
            document.removeEventListener('keydown', handleKeyDown);
        };
    }, [isActionMenuOpen, closeActionMenu]);

    useEffect(() => {
        if (disabled || isStreaming) {
            setIsActionMenuOpen(false);
        }
    }, [disabled, isStreaming]);

    const handleDragOver = (event: DragEvent<HTMLDivElement>) => {
        if (disabled || !hasFiles(event)) {
            return;
        }

        event.preventDefault();
        setIsDragging(true);
    };

    const handleDragLeave = (event: DragEvent<HTMLDivElement>) => {
        if (event.currentTarget.contains(event.relatedTarget as Node)) {
            return;
        }

        setIsDragging(false);
    };

    const handleDrop = (event: DragEvent<HTMLDivElement>) => {
        if (disabled) {
            return;
        }

        if (!hasFiles(event)) {
            return;
        }

        event.preventDefault();
        setIsDragging(false);
        const files = Array.from(event.dataTransfer.files ?? []);
        if (files.length) {
            onAttachImages?.(files);
        }
    };

    const hasText = value.trim().length > 0;
    const hasImages = images.length > 0;
    const hasContent = hasText || hasImages;

    const getImagePreviewSource = (image: UserImageUpload): string => {
        const cachedPreview = imagePreviewCacheRef.current.get(image.id);
        if (cachedPreview && cachedPreview.base64 === image.base64 && cachedPreview.mimeType === image.mimeType) {
            return cachedPreview.source;
        }

        const source = createImagePreviewSource(image);
        imagePreviewCacheRef.current.set(image.id, {
            base64: image.base64,
            mimeType: image.mimeType,
            source
        });

        return source;
    };

    return (
        <section className={styles.inputArea} aria-label="Message input area">
            <div
                className={`${styles.composerShell} ${isDragging ? styles.composerShellDrag : ''}`}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
            >
                <div className={styles.composerBody}>
                    {shouldShowTaskPlan && taskPlan ? <TaskPlanPanel plan={taskPlan} /> : null}
                    <label className={styles.visuallyHidden} htmlFor={textareaId}>
                        {t('placeholder')}
                    </label>
                    <textarea
                        id={textareaId}
                        className={styles.messageTextarea}
                        value={value}
                        rows={MIN_VISIBLE_LINES}
                        placeholder={t('placeholder')}
                        disabled={disabled}
                        aria-disabled={disabled}
                        ref={textareaRef}
                        autoComplete="off"
                        autoCorrect="off"
                        autoCapitalize="off"
                        onChange={handleChange}
                        onKeyDown={handleKeyDown}
                        onPaste={handlePaste}
                    />
                </div>
                <div className={styles.attachmentTray}>
                    {hasImages ? (
                        <ul className={styles.attachmentList}>
                            {images.map((image) => (
                                <li key={image.id} className={styles.attachmentPreview}>
                                    <img
                                        className={styles.attachmentThumbnail}
                                        src={getImagePreviewSource(image)}
                                        alt={image.fileName}
                                        loading="lazy"
                                    />
                                    <button
                                        type="button"
                                        className={styles.removeAttachmentButton}
                                        onClick={() => onRemoveImage?.(image.id)}
                                        aria-label={t('removeImage')}
                                        disabled={disabled || isStreaming}
                                    >
                                        <AttachmentRemoveIcon />
                                    </button>
                                </li>
                            ))}
                        </ul>
                    ) : null}
                    {imageError ? (
                        <p className={styles.attachmentError} role="status">
                            {imageError}
                        </p>
                    ) : null}
                </div>
                <div className={styles.composerFooter}>
                    <div className={styles.composerControls}>
                        {showMediaControls ? (
                            <div className={styles.actionMenuWrapper}>
                                <button
                                    ref={actionMenuButtonRef}
                                    className={styles.inputActionButton}
                                    type="button"
                                    aria-label={t('moreActions')}
                                    aria-haspopup="menu"
                                    aria-expanded={isActionMenuOpen}
                                    data-tooltip={t('moreActions')}
                                    onClick={handleActionMenuToggle}
                                    disabled={disabled || isStreaming}
                                >
                                    <PlusIcon />
                                </button>
                                {isActionMenuOpen ? (
                                    <div className={styles.actionMenu} role="menu" ref={actionMenuRef}>
                                        <button
                                            type="button"
                                            role="menuitem"
                                            onClick={() => {
                                                closeActionMenu();
                                                handleAttachButtonClick();
                                            }}
                                            disabled={disabled || isStreaming}
                                        >
                                            <ImageIcon />
                                            <span>{t('attachImages')}</span>
                                        </button>
                                    </div>
                                ) : null}
                            </div>
                        ) : null}
                        <button
                            className={styles.inputActionButton}
                            type="button"
                            aria-label={t('resetConversation')}
                            data-tooltip={t('resetConversation')}
                            onClick={() => onReset?.()}
                            disabled={disabled || isStreaming || !onReset}
                        >
                            <ResetIcon />
                        </button>
                        {showDebugCopyButton ? (
                            <button
                                className={styles.inputActionButton}
                                type="button"
                                aria-label={t('copyConversationJsonResponses')}
                                data-tooltip={t('copyConversationJsonResponses')}
                                onClick={() => void onCopyConversationResponses?.()}
                                disabled={disabled || !onCopyConversationResponses || !hasConversationResponses}
                            >
                                <DebugJsonCopyIcon />
                            </button>
                        ) : null}
                        {showMediaControls ? (
                            <>
                                <input
                                    ref={fileInputRef}
                                    type="file"
                                    accept="image/*"
                                    multiple
                                    className={styles.fileInput}
                                    onChange={handleFilesSelected}
                                    aria-hidden="true"
                                    tabIndex={-1}
                                />
                            </>
                        ) : null}
                    </div>
                    {isStreaming ? (
                        <button
                            className={`${styles.sendButton} ${styles.cancelButton}`}
                            type="button"
                            aria-label={t('cancel')}
                            data-tooltip={t('cancel')}
                            onClick={handleCancel}
                        >
                            <StopIcon />
                        </button>
                    ) : hasContent && !disabled ? (
                        <button
                            className={styles.sendButton}
                            type="button"
                            aria-label={t('send')}
                            data-tooltip={t('send')}
                            onClick={handleSend}
                        >
                            <SendIcon />
                        </button>
                    ) : null}
                </div>
            </div>
        </section>
    );
}

function isMenuPointerEvent(
    event: MouseEvent,
    actionMenu: HTMLDivElement | null,
    actionMenuButton: HTMLButtonElement | null
): boolean {
    const eventPath = event.composedPath?.() ?? [];
    if (eventPath.length > 0) {
        return (
            (actionMenu ? eventPath.includes(actionMenu) : false) ||
            (actionMenuButton ? eventPath.includes(actionMenuButton) : false)
        );
    }

    const target = event.target as Node | null;
    if (!target) {
        return false;
    }

    return (
        (actionMenu ? actionMenu.contains(target) : false) ||
        (actionMenuButton ? actionMenuButton.contains(target) : false)
    );
}

type CachedImagePreview = {
    base64: string;
    mimeType: string;
    source: string;
};

function hasFiles(event: DragEvent<HTMLElement>): boolean {
    return Array.from(event.dataTransfer?.items ?? []).some((item) => item.kind === 'file');
}

function createImagePreviewSource(image: UserImageUpload): string {
    return `data:${image.mimeType};base64,${image.base64}`;
}
